import { Badge, Button, FlexBox, Spinner } from '@dixa/lib-shared-habla';
import { Message as MessageT } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import { useCallback, useMemo } from 'react';

import { auditLogsApi, ConversationAuditLogEntry } from 'apis/auditLogs';
import { getMessageSanitizationChannel } from 'app/utils/htmlSanitizer';
import { useTranslatePlainTextConversationMessage } from 'assets/components/conversation/components/messages/utils/useTranslatePlainTextConversationMessage';
import { TranslationBadge } from 'components/translationBadge';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint/useLazyPaginatedEndpoint';

import { Message } from '../../../conversation/components/message';
import activityLogStyles from '../activityLog.module.scss';

import { getLogEntryAuthorName, LogEntry } from './logEntry';
import styles from './logEntry/logEntry.module.scss';
import { MessageActivity } from './messageActivity';
import { SidekickCustomerMessageButton } from './sidekickDebugButton/sidekickCustomerMessageButton';
import { SidekickReplyDebugButton } from './sidekickDebugButton/sidekickReplyDebugButton';
import { UserLink } from './userLink/userLink';

// When the activity log backend started writing events on the message
// resource, so messages added before it have no activity to expand into.
const MESSAGE_ACTIVITY_LOGGED_SINCE = new Date('2026-10-30T00:00:00Z');

type MessageAddedProps = {
  entry: ConversationAuditLogEntry<
    'MESSAGE_ADDED_BY_AGENT' | 'MESSAGE_ADDED_BY_CUSTOMER'
  >;
  messages: Array<MessageT>;
};

type MessageAddedContentProps = {
  message: MessageT;
  author: User;
  isTranscript: boolean;
};

const MessageAddedContent = ({
  message,
  author,
  isTranscript,
}: MessageAddedContentProps): JSX.Element => {
  const { button: translateButton, translation } =
    useTranslatePlainTextConversationMessage({
      text: message.text ?? undefined,
      originalLanguageCode: message.language ?? null,
      contentType: message.content_type === 'Html' ? 'html' : 'text',
    });

  const displayMarkup =
    translation?.status === 'fulfilled' && translation.translatedText
      ? translation.translatedText
      : (message.text ?? undefined);

  const nameMenu =
    isTranscript || translation?.status ? (
      <>
        {isTranscript && (
          <Badge size="small" variant="info">
            Transcribed
          </Badge>
        )}
        {translation?.status && (
          <TranslationBadge
            translation={translation}
            originalLanguageCode={message.language ?? undefined}
            errorMessage="There was an error translating this message."
          />
        )}
      </>
    ) : undefined;

  return (
    <Message
      className={styles.message}
      author={author}
      markup={displayMarkup}
      date=""
      channel={getMessageSanitizationChannel(message)}
      nameMenu={nameMenu}
      removeGutter
      gutterButtons={translateButton}
    />
  );
};

export const MessageAdded = ({
  entry,
  messages,
}: MessageAddedProps): JSX.Element => {
  const {
    items: activities,
    changeArg,
    asyncStatus,
    hasNextPage,
    nextPage,
  } = useLazyPaginatedEndpoint({
    endpoint: auditLogsApi.endpoints.readMessageAuditLogs,
    itemsSelector: (response) => response.data.logs,
    infinite: true,
  });

  // Only fetch the message's activity the first time the user expands the
  // entry, to avoid firing a request for every message in the log.
  const handleExpandedChange = useCallback(
    (isExpanded: boolean) => {
      if (isExpanded && asyncStatus === 'uninitialized') {
        changeArg({
          resourceId: `${entry.resource.id}_${entry.message_id}`,
          limit: 1000,
          sort: { key: 'timestamp', direction: 'asc' },
        });
      }
    },
    [asyncStatus, changeArg, entry.resource.id, entry.message_id],
  );

  const authorName = getLogEntryAuthorName(entry);
  const authorLink = <UserLink userId={entry.author.id}>{authorName}</UserLink>;

  const userType =
    entry.activity_type === 'MESSAGE_ADDED_BY_AGENT' ? 'agent' : 'customer';

  const text = (
    <>
      Message is added by {userType} {authorLink}
    </>
  );
  const author: User = {
    id: entry.author.id,
    name: entry.author.name ?? null,
    email: entry.author.email ?? null,
    phone_number: entry.author.phone_number ?? null,
    avatar_url: entry.avatar_url ?? null,
    type: userType === 'agent' ? 'member' : 'contact',
    org_id: null,
    company_id: null,
    default_outbound_number: null,
    anonymized_at: null,
    given_name: null,
    family_name: null,
    has_scim_enabled: false,
    external_id: null,
  };

  const message = messages.find((message) => message.id === entry.message_id);
  // Entries logged before the audit log carried is_transcript fall back to the loaded message.
  const isTranscript = Boolean(entry.is_transcript ?? message?.is_transcript);

  // The AI agent that drafts a reply to a customer is the one that wrote in the
  // conversation most recently.
  const authorIds = useMemo(
    () => [
      ...new Set(
        messages
          .map((item) => item.author?.id ?? item.author_id)
          .filter((id): id is string => Boolean(id) && id !== entry.author.id)
          .reverse(),
      ),
    ],
    [messages, entry.author.id],
  );

  const actions =
    userType === 'agent' ? (
      <SidekickReplyDebugButton
        authorId={entry.author.id}
        csid={entry.resource.id}
        messageId={entry.message_id}
        date={entry.event_timestamp}
      />
    ) : authorIds.length > 0 ? (
      <SidekickCustomerMessageButton
        authorIds={authorIds}
        csid={entry.resource.id}
        messageId={entry.message_id}
        date={entry.event_timestamp}
      />
    ) : undefined;

  return (
    <LogEntry
      entry={entry}
      text={text}
      actions={message?.text ? actions : undefined}
      content={
        message?.text ? (
          <MessageAddedContent
            message={message}
            author={author}
            isTranscript={isTranscript}
          />
        ) : null
      }
      isExpandable={
        new Date(entry.event_timestamp) >= MESSAGE_ACTIVITY_LOGGED_SINCE
      }
      onExpandedChange={handleExpandedChange}
    >
      <FlexBox
        padding="0 0 0 300"
        margin="0 0 0 500"
        gap="200"
        flexDirection="column"
      >
        {asyncStatus === 'rejected' && (
          <span className={activityLogStyles.error}>
            Failed to retrieve message activity log.
          </span>
        )}

        {activities.map((activity) => (
          <MessageActivity key={activity.event_id} entry={activity} />
        ))}

        {asyncStatus === 'pending' && !activities.length && (
          <div
            className={activityLogStyles.spinnerContainer}
            data-testid="spinner"
          >
            <Spinner />
          </div>
        )}

        {activities.length > 0 && hasNextPage && (
          <FlexBox margin="200 0">
            <Button
              variant="secondary"
              isLoading={asyncStatus === 'pending'}
              onClick={nextPage}
            >
              Load more
            </Button>
          </FlexBox>
        )}
      </FlexBox>
    </LogEntry>
  );
};
